import { isUsGameSport, usBaseSport, type UsGameSport } from './sports.js';
import { usEventIdentity, type UsGameIdentity } from './gameIdentity.js';

export const US_GAME_INDEX_MAX_DAYS = 31;
export const US_GAME_INDEX_MAX_GAMES = 256;
export interface UsGameIndexQuery { sport: UsGameSport; start?: string; end?: string }
export interface UsGameIndexPage {
  sport: UsGameSport; games: readonly UsGameIdentity[];
  complete: boolean; truncated: boolean; excludedRows: number;
}
const day = (value: unknown): value is string => typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
  && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
export function validUsGameIndexQuery(q: UsGameIndexQuery): boolean {
  return isUsGameSport(q.sport) && (q.start === undefined && q.end === undefined
    || day(q.start) && day(q.end) && q.start <= q.end
      && (Date.parse(q.end) - Date.parse(q.start)) / 86_400_000 < US_GAME_INDEX_MAX_DAYS);
}
/** Only full-game native S/T candidates seed an owner query. Missing navigation
 * metadata does not invalidate that row's existing market or book admission. */
export function isUsLineSeedCandidate(value: unknown): boolean {
  const r = value as Record<string, unknown> | null, sport = usBaseSport(r?.sport);
  return !!r && !!sport && r.marketType === 'game' && r.period === 'full'
    && (r.pmusMarketType === 'spreads' && typeof r.sportsMarketType === 'string' && /full_game_spread$/.test(r.sportsMarketType)
      || r.pmusMarketType === 'totals' && (sport === 'NBA' && r.sportsMarketType === 'basketball_team_full_game_total'
        || (sport === 'NFL' || sport === 'CFB') && r.sportsMarketType === 'football_team_full_game_total'));
}
export function usLineGameSeed(value: unknown): UsGameIdentity | null {
  const r = value as Record<string, unknown> | null, game = usEventIdentity(value);
  if (!r || !game || !isUsLineSeedCandidate(r) || !day(game.key.split('|')[1]) || r.period !== 'full'
    || typeof r.floorStrike !== 'number' || !Number.isFinite(r.floorStrike)) return null;
  if (r.pmusMarketType === 'spreads' && typeof r.sportsMarketType === 'string'
    && /full_game_spread$/.test(r.sportsMarketType) && r.floorStrike !== 0
    && [game.awayCode, game.homeCode].includes(r.canonicalYesTeam as string)) return game;
  if (r.pmusMarketType === 'totals' && r.floorStrike > 0 && r.canonicalYesTeam == null
    && (game.sport === 'NBA' && r.sportsMarketType === 'basketball_team_full_game_total'
      || (game.sport === 'NFL' || game.sport === 'CFB') && r.sportsMarketType === 'football_team_full_game_total')) return game;
  return null;
}
interface Member { sport: UsGameSport; candidate: boolean; game: UsGameIdentity | null }
interface Bucket {
  groups: Map<string, Map<string, { count: number; game: UsGameIdentity }>>;
  cached: readonly UsGameIdentity[]; conflicts: number; total: number;
}
/** Derived during existing catalogue membership work. Reads visit at most 31
 * date buckets and copy 256 games; they never scan markets or rebuild indexes.
 * Book frames never call this cache. A live identity change rebuilds only its
 * old/new day buckets, while replacement batches one rebuild per touched day. */
export class UsGameSeedIndex {
  private members = new Map<string, Member>();
  private sports = new Map<UsGameSport, Map<string, Bucket>>();
  private days = new Map<UsGameSport, readonly string[]>();
  private excluded = new Map<UsGameSport, number>();
  private dirty = new Set<Bucket>();
  private dirtyGames = new Set<string>();
  private variants = new Map<string, Map<string, number>>();
  clear(): void { this.members.clear(); this.sports.clear(); this.days.clear(); this.excluded.clear(); this.dirty.clear(); this.variants.clear(); this.dirtyGames.clear(); }
  set(slug: string, next: Member): void {
    this.remove(slug);
    if (!next.candidate) return;
    this.members.set(slug, next);
    if (!next.game) { this.excluded.set(next.sport, (this.excluded.get(next.sport) ?? 0) + 1); return; }
    const game = Object.freeze({ ...next.game }), date = new Date(game.startTimeMs).toISOString().slice(0, 10);
    let dates = this.sports.get(game.sport);
    if (!dates) { dates = new Map(); this.sports.set(game.sport, dates); }
    let bucket = dates.get(date);
    if (!bucket) { bucket = { groups: new Map(), cached: [], conflicts: 0, total: 0 }; dates.set(date, bucket); this.days.delete(game.sport); }
    let group = bucket.groups.get(game.key);
    if (!group) { group = new Map(); bucket.groups.set(game.key, group); }
    const signature = JSON.stringify(game), existing = group.get(signature);
    const variants = this.variants.get(game.key) ?? new Map<string, number>();
    variants.set(signature, (variants.get(signature) ?? 0) + 1); this.variants.set(game.key, variants);
    this.dirtyGames.add(game.key);
    group.set(signature, { game, count: (existing?.count ?? 0) + 1 }); this.dirty.add(bucket);
  }
  remove(slug: string): void {
    const old = this.members.get(slug); if (!old) return;
    this.members.delete(slug);
    if (!old.candidate) return;
    if (!old.game) { this.excluded.set(old.sport, Math.max(0, (this.excluded.get(old.sport) ?? 0) - 1)); return; }
    const game = old.game, date = new Date(game.startTimeMs).toISOString().slice(0, 10), dates = this.sports.get(game.sport);
    const bucket = dates?.get(date), group = bucket?.groups.get(game.key), signature = JSON.stringify(game), previous = group?.get(signature);
    if (!bucket || !group || !previous) return;
    const variants = this.variants.get(game.key);
    if (variants) {
      const count = variants.get(signature) ?? 0;
      if (count > 1) variants.set(signature, count - 1); else variants.delete(signature);
      if (!variants.size) this.variants.delete(game.key);
      this.dirtyGames.add(game.key);
    }
    if (previous.count > 1) group.set(signature, { ...previous, count: previous.count - 1 }); else group.delete(signature);
    if (!group.size) bucket.groups.delete(game.key);
    if (!bucket.groups.size) { dates!.delete(date); this.days.delete(game.sport); }
    this.dirty.add(bucket);
  }
  finish(): void {
    for (const key of this.dirtyGames) for (const signature of this.variants.get(key)?.keys() ?? []) {
      const game = JSON.parse(signature) as UsGameIdentity;
      const bucket = this.sports.get(game.sport)?.get(new Date(game.startTimeMs).toISOString().slice(0, 10));
      if (bucket) this.dirty.add(bucket);
    }
    this.dirtyGames.clear();
    for (const bucket of this.dirty) {
      const games: UsGameIdentity[] = []; let conflicts = 0;
      for (const [key, variants] of bucket.groups) {
        if (this.variants.get(key)?.size !== 1) for (const row of variants.values()) conflicts += row.count;
        else games.push(variants.values().next().value!.game);
      }
      games.sort((a, b) => a.startTimeMs - b.startTimeMs || a.key.localeCompare(b.key));
      bucket.total = games.length; bucket.conflicts = conflicts;
      bucket.cached = Object.freeze(games.slice(0, US_GAME_INDEX_MAX_GAMES));
    }
    this.dirty.clear();
    for (const [sport, dates] of this.sports) if (!this.days.has(sport)) this.days.set(sport, Object.freeze([...dates.keys()].sort()));
  }
  read(q: UsGameIndexQuery): UsGameIndexPage {
    const available = this.days.get(q.sport) ?? [], dates: string[] = [];
    // Finite date windows do direct map lookups, never a whole-sport scan.
    if (q.start && q.end) for (let at = Date.parse(q.start); at <= Date.parse(q.end); at += 86_400_000) dates.push(new Date(at).toISOString().slice(0, 10));
    else dates.push(...available.slice(0, US_GAME_INDEX_MAX_DAYS));
    const games: UsGameIdentity[] = []; let truncated = !q.start && available.length > US_GAME_INDEX_MAX_DAYS;
    let excludedRows = this.excluded.get(q.sport) ?? 0;
    for (const date of dates) {
      const bucket = this.sports.get(q.sport)?.get(date); if (!bucket) continue;
      excludedRows += bucket.conflicts;
      const remaining = US_GAME_INDEX_MAX_GAMES - games.length;
      games.push(...bucket.cached.slice(0, remaining));
      if (bucket.total > remaining) truncated = true;
    }
    return { sport: q.sport, games, complete: !truncated && excludedRows === 0, truncated, excludedRows };
  }
}
